import React, { useEffect, useRef , useState } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import LabelNumber from "../../../styled-components/LabelNumber";
import { useTheme } from "../../../contexts/ThemeContext";
import LabelText from "../../../ui/LabelText";
import EmptyDataButton from "../../../ui/EmptyDataButton";
import LargeModal from "../../../ui/LargeModal";

const TableRow = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;
const StyledChartWrapper = styled.div`
  width: 100%;
  //padding-bottom: 20px;

  & .chart-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 60px;
  }

  & .chart-container {
    position: relative;
    height: max-content;
    display: flex;
    align-items: center;
  }

  & .legend-container {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;
    flex-direction: column;
    align-items: baseline;
  }
  & .legend-container .legend {
    margin: 0px;
  }

  & .legend {
    text-transform: uppercase;
    font-size: 15px;
    font-family: "DM Mono", monospace;
    font-style: normal;
    display: flex;
    gap: 5px;
    align-items: center;
  }
`;

interface BarChartData {
    name: string;
    percent: number;
    valueText?: string | number;
    color: string;
    chart?: {
        path: any; // Update this type to match the actual type of your SVG path
        arc: any; // Update this type to match the actual type of your D3 arc
    };

}

interface BarChartProps {
    data: BarChartData[];
    isFlex?: boolean
}

const SeasonBreakdownBarChart: React.FC<BarChartProps> = ({ data, isFlex }) => {
    const { theme } = useTheme();
    const chartRef = useRef<SVGSVGElement>(null);

    const isAllNull = data?.every(item => item.valueText === null);
    const isPartialNull = data?.some(item => item.valueText === null) && !isAllNull;

    const [isLargeModalOpen, setLargeModalIsOpen] = useState(false);

    const toggleLargeModal = () => {
        setLargeModalIsOpen(!isLargeModalOpen);
    }

    const modalData = {
        "headerTitle": "Data not provided by club",
        "actionTag": " ",
        "items": [
            {
                "subTitle": " ",
                "description": "Leeds United are situated in a country who’s laws to not require them to make public the data we’d need to fully calculate this sum.",

            },
        ],
    }

    useEffect(() => {
        // This useEffect hook is responsible for rendering the bar chart
        if (!chartRef.current || data.length === 0) return;

        const width = 300;
        const height = 300;
        const margin = { top: 20, right: 70, bottom: 50, left: 20 };
        // Set the dimensions of the chart and the margins
        const fixedBarSpacing = 10;
        const barWidth = 55;
        // Define the spacing between bars and the width of each bar

        const svg = d3
            .select(chartRef.current)
            .attr("width", width)
            .attr("height", height);
        // Select the chartRef.current element and create an SVG container with the specified width and height

        const maxPercent = d3.max(data, (d: any) => d.percent) || 100;


        const x = d3
            .scaleBand()
            .range([margin.left, width - margin.right])
            .domain(data.map((d) => d.name));
        // Create the x-scale, a band scale, to map the data names to the horizontal axis
        const y = d3.scaleLinear()
            .range([height - margin.bottom, margin.top])
            .domain([0, maxPercent]);
        // Create the y-scale, a linear scale, to map the percentage values to the vertical axis
        // Set the y-scale domain to be from 0 to 100
        svg.selectAll("*").remove();

        // Draw background lines
        const numberOfLines = 5;
        const stepSize = maxPercent / (numberOfLines - 1);
        const lineValues = Array.from({ length: numberOfLines }, (_, i) => i * stepSize);

        svg.selectAll(".line")
            .data(lineValues)
            .enter()
            .append("line")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("stroke", theme.playerDataBorder)
            .attr("stroke-width", 0.8);
        // Render the background horizontal lines at the 0%, 25%, 50%, 75%, and 100% marks

        // Draw bars
        svg.selectAll(".bar")
            .data(data)
            .enter()
            .append("path")
            .attr("class", "bar")
            .attr("d", (d, i) => {
                // Calculate the position and dimensions of each bar
                const barX = margin.left + i * (barWidth + fixedBarSpacing);
                const barY = y(d.percent);
                const barHeight = height - margin.bottom - y(d.percent);
                const maxRadius = Math.min(barWidth, barHeight) / 2;
                //const scaledRadius = d.percent > 0 ? (d.percent / d3.max(data, (d: any) => d.percent)) * maxRadius + 7 : 0;
                const scaledRadius =
                    d.percent > lineValues[1]
                        ? 33
                        : d.percent > 0 && d.percent != null && d.percent < lineValues[1]
                            ? (d.percent / lineValues[1]) * 33 + 5
                            : 0;
                // Determine the rounded corner radius for the bar based on the percentage value
                return `M${barX + scaledRadius},${barY}
                        Q${barX},${barY} ${barX},${barY + scaledRadius}
                        V${barY + barHeight}
                        H${barX + barWidth}
                        V${barY + scaledRadius}
                        Q${barX + barWidth},${barY} ${barX + barWidth - scaledRadius},${barY}
                        H${barX + scaledRadius}
                        Z`;
            })
            .attr("fill", (d) => d.color);

        // Draw bottom shapes.
        if(!isAllNull){
            svg
            .append("g")
            .selectAll("path")
            .data(data)
            .enter()
            .append("path")
            .attr("d", (d, i) => {
                // Calculate the position and dimensions of the bottom rectangle for each bar
                const barX = margin.left + i * (barWidth + fixedBarSpacing);
                const barY = height - margin.bottom - 1;
                const bottomRightX = barX + barWidth;
                const bottomRightY = barY + 50;
                const scaledRadius = d.percent > 0 ? 0 : 15;
                const leftBottomRadius = 5; // Set the desired left bottom radius value
                return `M${barX + scaledRadius},${barY} 
                        Q${barX},${barY} ${barX},${barY + scaledRadius},${barX},${barY} ${barX},${barY + scaledRadius}  
                        L${barX},${bottomRightY - leftBottomRadius} 
                        Q${bottomRightX},${bottomRightY} ${bottomRightX},${barY} 
                        Z`;
            })
            .attr("fill", (d) => d.color);
        }
        
        // Render the rounded rectangular shapes at the bottom of each bar to represent the bar labels

        if (isAllNull) {
 
            // Add central text
            svg.append("rect")
                .attr("class", 'rect')
                .attr("x", width / 5)
                .attr("y", height /2 + (-60))
                .attr("text-anchor", "middle")
                .attr("width", 190)
                .attr("height", 35)
                .style("stroke", theme.emptyDataBorder)
                .style("cursor", "pointer")
                .style("fill", theme.playerDataBorder)
                .on('click', (event, d) => {
                    setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
                })
            // Set text color
            const displayText = isAllNull 
            ? 'Data not provided by club'
            : 'Full Data not provided by club';

            svg.append("text")
                // .data(data!)
                .attr("x", width / 2 - 10)
                .attr("y", height / 2 + (-38))
                .attr("text-anchor", "middle") // Center the text
                .style("font-family", "Aeonik") // Set font family to Aeonik
                .style("font-size", "12px") // Set font size
                .style("font-weight", 500)
                .style("cursor", "pointer")
                .style("fill", theme.playerColor) // Set text color
                .text(displayText)
                .on('click', (event, d) => {
                    setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
                })

            // Calculate the position for the icon
            const iconX = (width / 2) + 68; // Adjust the X position as needed
            const iconY = (height / 2 + (-52)); // Adjust the Y position as needed

            // Append the icon (using SVG path data for HelpOutlineIcon)
            svg.append("path")
                .attr("d", "M 11 18 h 2 v -2 h -2 Z m 1 -16 C 6.48 2 2 6.48 2 12 s 4.48 10 10 10 s 10 -4.48 10 -10 S 17.52 2 12 2 m 0 18 c -4.41 0 -8 -3.59 -8 -8 s 3.59 -8 8 -8 s 8 3.59 8 8 s -3.59 8 -8 8 m 0 -14 c -2.21 0 -4 1.79 -4 4 h 2 c 0 -1.1 0.9 -2 2 -2 s 2 0.9 2 2 c 0 2 -3 1.75 -3 5 h 2 c 0 -2.25 3 -2.5 3 -5 c 0 -2.21 -1.79 -4 -4 -4")
                .attr("transform", `translate(${iconX}, ${iconY})`)
                .attr("transform", `translate(${iconX}, ${iconY}) scale(0.7)`) // Scale down by 50%
                .style("fill", theme.playerColor)
                .on('click', (event, d) => {
                    setLargeModalIsOpen(!isLargeModalOpen); // Example alert, you can use any property from the data
                })
        }

    }, [data, theme]);


    return (
        <>
        <LargeModal toggleLargeModal={toggleLargeModal}
                isOpen={isLargeModalOpen}
                data={modalData} />
        <StyledChartWrapper id="chart-wrapper">
            <div className={isFlex ? "" : "chart-wrapper"}>
                <div id="chart-container" className="chart-container">
                    <svg ref={chartRef} style={{borderBottom: data.length > 0 ? `1px solid ${theme.playerDataBorder}` : ""}}></svg>

                </div>
                {!isFlex ? (
                    <div id="legend-container" className="legend-container">
                        {data.map(
                            (d) =>
                                // d.percent > 0 && (
                                <React.Fragment key={d.name}>
                                    {d.name !== "Score" && (
                                        <p className="legend">
                                            <span
                                                style={{
                                                    backgroundColor: d.color,
                                                    width: 10,
                                                    height: 10,
                                                    color: d.color,
                                                    borderRadius: "50%",
                                                }}
                                            ></span>
                                            <LabelNumber className="attack-defense-data">{d.name}{" "}
                                                <LabelNumber className="chart-content-number">
                                                    {d.percent > 0 ? d.valueText : '-'}
                                                </LabelNumber>
                                            </LabelNumber>
                                        </p>
                                    )}
                                </React.Fragment>
                            // )
                        )}
                    </div>
                ) : (
                    <div className='stat-table-container'>
                        <div className='stat-table-column'>
                            {data?.map((d, index) => (
                                <TableRow key={index} className="sb-table-row">
                                    <span
                                        style={{
                                            backgroundColor: d.color,
                                            width: 12,
                                            height: 12,
                                            color: d.color,
                                            borderRadius: "50%",
                                            display: 'inline-block',
                                            marginRight: 8,
                                        }}
                                    ></span>
                                    <LabelText className="selected-zone-name">{d.name}</LabelText>
                                    {/* <LabelNumber className="chart-content-number">€{d.valueText}</LabelNumber> */}
                                    {/* {d.valueText === null ? (
                                        // <div style={{marginLeft:"auto"}}>
                                        // <EmptyDataButton label="Data not provided by club"/>
                                        // </div>
                                        <LabelNumber className="chart-content-number">--</LabelNumber>
                                    ) : (
                                        <LabelNumber className="chart-content-number">€{d.valueText}</LabelNumber>
                                    )} */}
                             {
                                    isAllNull ? <LabelNumber className="chart-content-number">--</LabelNumber> :
                                        isPartialNull && d.valueText === null ? <div style={{ marginLeft: "auto" }}>
                                            <EmptyDataButton label="Data not provided by club" />
                                        </div> : <LabelNumber className="chart-content-number">€{d.valueText}</LabelNumber>
                                }


                                </TableRow>
                            ))}
                        </div>
                    </div>
                )}


            </div>
        </StyledChartWrapper>
        </>
    );
};

export default SeasonBreakdownBarChart;
